import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { css, StyleSheet } from 'aphrodite'
import { COLOR_WHITE } from '../../lib/colors'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import END_REQUEST from '../../graphql/Requests/mutations/EndRequest'
import dayjs from 'dayjs'
import { MEDIA_MIN_SM } from '../../lib/breakpoints'

const styles = StyleSheet.create({
  container: {
    width: '100%',
    minWidth: '50%',
    height: '100%',
    backgroundColor: COLOR_WHITE,
    [MEDIA_MIN_SM]: {
      justifyContent: 'center',
      display: 'flex',
    },
  },
})

export const Participant = ({
  participant,
  isMuted,
  setDurationLive,
  handleLogout,
  setIsMakerOut,
}) => {
  const [videoTracks, setVideoTracks] = useState([])
  const [audioTracks, setAudioTracks] = useState([])
  const [isPortrait, setPortrait] = useState(true)

  const videoRef = useRef()
  const audioRef = useRef()

  const { t } = useTranslation('translation')

  const [endRequest] = useMutation(END_REQUEST, {
    onCompleted: () => {
      toast.success(t('pages.live.addMoreTimeSuccess'))
      setDurationLive((prev) => prev + 1)
    },
    onError: () => {
      toast.error(t('pages.live.addMoreTimeError'))
    },
  })

  const trackpubsToTracks = (trackMap) =>
    Array.from(trackMap.values())
      .map((publication) => publication.track)
      .filter((track) => track !== null)

  useEffect(() => {
    setVideoTracks(trackpubsToTracks(participant.videoTracks))
    setAudioTracks(trackpubsToTracks(participant.audioTracks))

    const trackSubscribed = (track) => {
      if (track.kind === 'video') {
        setVideoTracks((videoTracks) => [...videoTracks, track])
      } else if (track.kind === 'audio') {
        setAudioTracks((audioTracks) => [...audioTracks, track])
      } else if (track.kind === 'data') {
        track.on('message', (data) => {
          if (data === 'yes') {
            toast.success(t('pages.live.addMoreTimeAccepted'), {
              onClose: toast.dismiss(),
            })
            const dateTimeRangeLive = JSON.parse(
              localStorage.getItem('dateTimeRangeLive')
            )
            const idRequest = localStorage.getItem('idRequest')
            endRequest({
              variables: {
                input: {
                  id: idRequest,
                  patch: {
                    status: 'OVER',
                    datetimeRange: {
                      start: {
                        value: dateTimeRangeLive.start.value,
                        inclusive: true,
                      },
                      end: {
                        value: dayjs(dateTimeRangeLive.end.value)
                          .add(1, 'hour')
                          .toISOString(),
                        inclusive: true,
                      },
                    },
                  },
                },
              },
            })
          } else if (data === 'no') {
            toast.error(t('pages.live.addMoreTimeDeclined'), {
              onClose: toast.dismiss(),
            })
          } else if (data === 'LANDSCAPE-RIGHT' || data === 'LANDSCAPE-LEFT') {
            setPortrait(false)
          } else if (data === 'PORTRAIT') {
            setPortrait(true)
          } else if (data === 'endLive') {
            handleLogout()
          } else if (data === 'inactive') {
            setIsMakerOut(true)
          } else if (data === 'active') {
            setIsMakerOut(false)
          }
        })
      }
    }

    const trackUnsubscribed = (track) => {
      if (track.kind === 'video') {
        setVideoTracks((videoTracks) => videoTracks.filter((v) => v !== track))
      } else if (track.kind === 'audio') {
        setAudioTracks((audioTracks) => audioTracks.filter((a) => a !== track))
      }
    }

    participant.on('trackSubscribed', trackSubscribed)
    participant.on('trackUnsubscribed', trackUnsubscribed)

    return () => {
      setVideoTracks([])
      setAudioTracks([])
      participant.removeAllListeners()
    }
  }, [participant])

  useEffect(() => {
    const videoTrack = videoTracks[0]
    if (videoTrack) {
      videoTrack.attach(videoRef.current)
      return () => {
        videoTrack.detach()
      }
    }
  }, [videoTracks])

  useEffect(() => {
    const audioTrack = audioTracks[0]
    if (audioTrack) {
      audioTrack.attach(audioRef.current)
      return () => {
        audioTrack.detach()
      }
    }
  }, [audioTracks])

  return (
    <div className={css(styles.container)}>
      <video
        style={{ minWidth: isPortrait ? '50vw' : '100vw', height: '100vh' }}
        ref={videoRef}
        autoPlay={true}
      />
      <audio ref={audioRef} autoPlay={true} muted={isMuted} />
    </div>
  )
}

Participant.propTypes = {
  participant: PropTypes.any,
  isMuted: PropTypes.bool,
  setDurationLive: PropTypes.any,
  handleLogout: PropTypes.func,
  setIsMakerOut: PropTypes.func,
}
